/**
 * AI Story Studio — lightweight hub linking to existing story tools.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  Download,
  Loader2,
  Mic,
  PenLine,
  Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import AppFooter from '../components/AppFooter';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import {
  STORY_STUDIO_HUB_CARDS,
  STORY_STUDIO_SAFETY,
  STORY_STUDIO_TAGLINE,
} from '../content/storyStudioContent';
import { useAuth } from '../contexts/AuthContext';
import { getMyArchivePersonId } from '../utils/myArchivePerson';
import { personHref, treeHref } from '../utils/prettyArchiveLinks';
import { storybookApi, storybookHref, storybookStartHref } from '../utils/storybook';
import axios from 'axios';
import { getApiBase } from '../utils/apiBase';
import StartForMePromptBuilder from '../components/storyStudio/StartForMePromptBuilder';
import SavedPromptsSection from '../components/storyStudio/SavedPromptsSection';
import { trackProductEvent } from '../utils/productEvents';

const API = getApiBase();

const CARD_ICONS = {
  storybook: BookOpen,
  voice: Mic,
  polish: PenLine,
  'legacy-book': Download,
};

function resolveHubLink(card, { myPersonId, firstTree, readinessTree }) {
  if (card.route) return card.route;
  switch (card.resolve) {
    case 'personStoriesVoice':
      return myPersonId
        ? `${personHref({ id: myPersonId })}?view=stories&focus=voice`
        : '/storybook';
    case 'personStories':
      return myPersonId ? `${personHref({ id: myPersonId })}?view=stories` : '/storybook';
    case 'treeReadiness':
      if (readinessTree) return `${treeHref(readinessTree)}?tab=readiness`;
      if (firstTree) return `${treeHref(firstTree)}?tab=settings`;
      return '/guide#exports';
    default:
      return '/story-studio';
  }
}

export default function StoryStudioPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [trees, setTrees] = useState([]);
  const [storybooks, setStorybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedRefresh, setSavedRefresh] = useState(0);
  const startForMeExpanded =
    searchParams.get('startForMe') === '1' ||
    (typeof window !== 'undefined' && window.location.hash === '#start-for-me');

  const myPersonId = user?.self_person_id || getMyArchivePersonId();
  const firstTree = trees[0] || null;
  const readinessTree = trees.find((t) => t.role === 'owner') || firstTree;

  useEffect(() => {
    trackProductEvent('story_studio_opened', { pillar: 'story_studio' });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: treeData }, sbList] = await Promise.all([
          axios.get(`${API}/trees`),
          storybookApi.list().catch(() => []),
        ]);
        if (!cancelled) {
          setTrees(Array.isArray(treeData) ? treeData : treeData?.trees || []);
          setStorybooks(Array.isArray(sbList) ? sbList : []);
        }
      } catch {
        if (!cancelled) {
          setTrees([]);
          setStorybooks([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const recentStorybooks = useMemo(
    () =>
      [...storybooks]
        .sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''))
        .slice(0, 3),
    [storybooks],
  );

  const defaultStorybookHref = firstTree
    ? storybookStartHref({
        treeId: firstTree.id,
        personId: myPersonId || undefined,
        type: myPersonId ? 'person' : 'self',
      })
    : '/storybook';

  return (
    <div className="min-h-screen bg-[hsl(42_28%_97%)]" data-testid="story-studio-page">
      <header className="border-b border-[hsl(38_16%_82%)] bg-[hsl(42_28%_99%)]">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#5c534a] hover:text-[#1c1917]">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Dashboard
          </Link>
          <BrandWordmark className="h-6 opacity-80" />
          <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
            <Link to="/guide#ai-story-studio" className="text-xs text-[#7a7268] hover:text-[#5c534a]">
              Feature Guide
            </Link>
            <span className="hidden text-[#d4cdc3] sm:inline" aria-hidden>|</span>
            <Link to="/help#story-studio" className="text-xs text-[#7a7268] hover:text-[#5c534a]">
              How Story Studio works
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-12">
        <div className="mb-10 max-w-2xl">
          <p className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8b7355]">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            AI Story Studio
          </p>
          <h1 className="font-serif text-3xl font-light text-[#1c1917] sm:text-4xl">
            Write, record or upload memories
          </h1>
          <p className="mt-3 text-[0.95rem] leading-relaxed text-[#5c534a]">{STORY_STUDIO_TAGLINE}</p>
          <p className="mt-2 text-xs text-[#7a7268]">{STORY_STUDIO_SAFETY}</p>
        </div>

        <StartForMePromptBuilder
          personId={myPersonId || undefined}
          defaultExpanded={startForMeExpanded}
          className="mb-8"
          onSaved={() => setSavedRefresh((n) => n + 1)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          {STORY_STUDIO_HUB_CARDS.map((card) => {
            const Icon = CARD_ICONS[card.id] || BookOpen;
            const href =
              card.id === 'storybook'
                ? defaultStorybookHref
                : resolveHubLink(card, { myPersonId, firstTree, readinessTree });
            return (
              <article
                key={card.id}
                className="flex flex-col rounded-2xl border border-[hsl(38_16%_82%)] bg-[hsl(42_28%_99%)] p-5 shadow-sm"
                data-testid={`story-studio-hub-${card.id}`}
              >
                <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-[#8b7355]/10 text-[#8b7355]">
                  <Icon className="h-4 w-4" aria-hidden />
                </span>
                <h2 className="font-serif text-lg text-[#1c1917]">{card.title}</h2>
                <p className="mt-1 flex-1 text-sm leading-relaxed text-[#5c534a]">{card.copy}</p>
                <Button asChild size="sm" className="mt-4 w-fit rounded-full bg-[#8B7355] hover:bg-[#7A6548]">
                  <Link to={href}>{card.button}</Link>
                </Button>
              </article>
            );
          })}
        </div>

        <section className="mt-10 rounded-2xl border border-[hsl(38_16%_82%)]/80 bg-[hsl(42_28%_99%)] p-5 sm:p-6" data-testid="story-studio-recent">
          <h2 className="font-serif text-lg text-[#1c1917]">Recent story activity</h2>
          {loading ? (
            <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading…
            </p>
          ) : recentStorybooks.length ? (
            <ul className="mt-4 space-y-3">
              {recentStorybooks.map((sb) => (
                <li key={sb.id}>
                  <Link
                    to={storybookHref(sb.id)}
                    className="flex items-center justify-between gap-3 rounded-xl border border-[hsl(38_14%_86%)]/70 px-4 py-3 transition hover:border-[#c9a961]/40 hover:bg-white/80"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-[#1c1917]">{sb.title || 'Storybook'}</p>
                      <p className="text-xs text-[#7a7268]">
                        {sb.completed_count ?? 0}/{sb.total_prompts ?? 16} prompts answered
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-[#8b7355]">Open</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm leading-relaxed text-[#5c534a]">
              Start with one memory. A few words or two minutes of voice is enough.
            </p>
          )}
        </section>

        <div className="mt-8">
          <SavedPromptsSection personId={myPersonId || null} refreshKey={savedRefresh} />
        </div>
      </main>

      <AppFooter />
    </div>
  );
}
